import { CalendarDate, DateValue } from '@internationalized/date';
import type { RangeValue } from '@react-types/shared';
import { DATE_RANGE_OPTIONS, IAnalysisDateRangeFormData, IVesselLocationsDateFormData, VESSEL_LOCATION_OPTIONS } from "../types/dateRangeTypes";
import { 
    trackExceptionWithContext
} from '../../../services/application-insights/appInsights';
import { 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../../../services/application-insights/appInsightsConstants';

/**
 * Validates if a string is a valid date in DD/MM/YYYY format
 */
export const isValidDate = (dateString: string): boolean => {
	if (!dateString) return false;

	const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
	const match = dateString.match(dateRegex);

	if (!match) return false;

	const day = parseInt(match[1], 10);
	const month = parseInt(match[2], 10);
	const year = parseInt(match[3], 10);

	const date = new Date(year, month - 1, day);

	return date.getFullYear() === year &&
		date.getMonth() === (month - 1) &&
		date.getDate() === day;
};

/**
 * Validates if a string is a valid year (4 digits)
 */
export const isValidYear = (yearString: string): boolean => {
	if (!yearString) return false;

	const year = parseInt(yearString, 10);
	const currentYear = new Date().getFullYear();

	return yearString.length === 4 &&
		!isNaN(year) &&
		year >= 1900 &&
		year <= currentYear + 10; // Allow future years up to 10 years
};

/**
 * Formats a date string to DD/MM/YYYY format
 */
export const formatDate = (date: Date): string => {
	const day = date.getDate().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	const year = date.getFullYear();

	return `${day}/${month}/${year}`;
};

/**
 * Formats a date string to DD-MMM-YYYY format
 */
export const formatDateWithMonth = (date: Date): string => {
	const day = date.getDate().toString().padStart(2, '0');
	const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
		'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	const month = monthNames[date.getMonth()];
	const year = date.getFullYear();

	return `${day}-${month}-${year}`;
};

/**
 * Gets the current date formatted as DD/MM/YYYY
 */
export const getCurrentDate = (): string => {
	return formatDate(new Date());
};

/**
 * Converts a CalendarDate to a JavaScript Date with time set to 23:59:00 (last minute of the day)
 * This ensures that when a user selects a specific date, it represents the end of that day rather than the beginning
 */
export const convertCalendarDateToEndOfDay = (calendarDate: CalendarDate): Date => {
	try {
		// Convert to Date at beginning of day in UTC
		const date = calendarDate.toDate('UTC');
		// Set time to 23:59:00 (last minute of the day)
		date.setUTCHours(23, 59, 0, 0);
		return date;
	} catch (error) {
		// Only log exceptions for date conversion failures
		trackExceptionWithContext(
			error instanceof Error ? error : new Error('Calendar date to end of day conversion failed'),
			{
				component: AppInsightsComponents.DATE_RANGE_HELPERS,
				additionalData: {
					errorType: AppInsightsErrorTypes.DATE_CONVERSION_ERROR,
					calendarDateYear: calendarDate?.year,
					calendarDateMonth: calendarDate?.month,
					calendarDateDay: calendarDate?.day
				}
			}
		);
		
		// Return current date as fallback
		const fallbackDate = new Date();
		fallbackDate.setHours(23, 59, 0, 0);
		return fallbackDate;
	}
};

export const calculateVesselLocationDate = (vesselLocationsDate: IVesselLocationsDateFormData): CalendarDate | null => {
	try {
		switch (vesselLocationsDate.selectedOption) {
			case VESSEL_LOCATION_OPTIONS.LATEST:
				// Handle latest option - return current date as CalendarDate
				const currentDate = new Date();
				return new CalendarDate(currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate()).add({ days: 1 });
			case VESSEL_LOCATION_OPTIONS.SPECIFIC:
				// Handle specific option - return the specific date as CalendarDate
				return vesselLocationsDate.specificDate ? vesselLocationsDate.specificDate : (() => {
					const fallbackDate = new Date();
					return new CalendarDate(fallbackDate.getFullYear(), fallbackDate.getMonth() + 1, fallbackDate.getDate()).add({ days: 1 }); // Default to current date if none selected
				})();
			default:
				const defaultDate = new Date();
				return new CalendarDate(defaultDate.getFullYear(), defaultDate.getMonth() + 1, defaultDate.getDate()).add({ days: 1 }); // Default to current date if option is unrecognized
		}
	} catch (error) {
		// Only log exceptions for date calculation failures
		trackExceptionWithContext(
			error instanceof Error ? error : new Error('Vessel location date calculation failed'),
			{
				component: AppInsightsComponents.DATE_RANGE_HELPERS,
				additionalData: {
					errorType: AppInsightsErrorTypes.DATE_RANGE_CALCULATION_ERROR,
					selectedOption: vesselLocationsDate.selectedOption,
					hasSpecificDate: !!vesselLocationsDate.specificDate
				}
			}
		);
		
		// Return fallback date
		const fallbackDate = new Date();
		return new CalendarDate(fallbackDate.getFullYear(), fallbackDate.getMonth() + 1, fallbackDate.getDate()).add({ days: 1 });
	}
};

export const calculateAnalysisDateRange = (
	analysisDateRange: IAnalysisDateRangeFormData,
	currentDateRange: RangeValue<DateValue>,
): RangeValue<DateValue> => {
	try {
		const currentDate = new Date();
		const currentCalendarDate = new CalendarDate(currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate());

		switch (analysisDateRange.selectedOption) {
			case DATE_RANGE_OPTIONS.YEAR_TO_DATE:
				// Handle year to date option
				return {
					start: new CalendarDate(new Date().getFullYear(), 1, 1),
					end: currentCalendarDate.add({ days: 1 }),
				};
			case DATE_RANGE_OPTIONS.PAST_3_YEARS:
				// Handle past 3 years option
				const threeYearsAgo = currentDateRange.end.toDate('UTC');
				threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
				return {
					start: new CalendarDate(threeYearsAgo.getFullYear(), threeYearsAgo.getMonth() + 1, 1),
					end: currentCalendarDate.add({ days: 1 }),
				};
			case DATE_RANGE_OPTIONS.SPECIFIC_YEAR:
				// Handle specific year option
				if (analysisDateRange.specificYear) {
					return {
						start: new CalendarDate(analysisDateRange.specificYear, 1, 1),
						end: new CalendarDate(analysisDateRange.specificYear, 12, 31).add({ days: 1 }),
					};
				}
				return {
					start: currentDateRange.start,
					end: currentCalendarDate.add({ days: 1 })
				};
			case DATE_RANGE_OPTIONS.SPECIFIC_RANGE:
				// Handle specific range option
				if (analysisDateRange.yearMonthRange) {
					const startYearMonth = analysisDateRange.yearMonthRange.start.toString();
					const endYearMonth = analysisDateRange.yearMonthRange.end.toString();

					const startYear = parseInt(startYearMonth.substring(0, 4));
					const startMonth = parseInt(startYearMonth.substring(4, 6));

					const endYear = parseInt(endYearMonth.substring(0, 4));
					const endMonth = parseInt(endYearMonth.substring(4, 6));

					// Get the last day of the end month
					const endDate = new Date(endYear, endMonth, 0);

					return {
						start: new CalendarDate(startYear, startMonth, 1),
						end: new CalendarDate(endYear, endMonth, endDate.getDate()).add({ days: 1 })
					};
				}
				return {
					start: currentDateRange.start,
					end: currentCalendarDate.add({ days: 1 })
				};
			default:
				return {
					start: currentDateRange.start,
					end: currentCalendarDate.add({ days: 1 })
				};
		}
	} catch (error) {
		// Only log exceptions for date range calculation failures
		trackExceptionWithContext(
			error instanceof Error ? error : new Error('Analysis date range calculation failed'),
			{
				component: AppInsightsComponents.DATE_RANGE_HELPERS,
				additionalData: {
					errorType: AppInsightsErrorTypes.DATE_RANGE_CALCULATION_ERROR,
					selectedOption: analysisDateRange.selectedOption,
					hasSpecificYear: !!analysisDateRange.specificYear,
					hasYearMonthRange: !!analysisDateRange.yearMonthRange
				}
			}
		);
		
		// Return fallback date range (current date)
		const fallbackDate = new Date();
		const fallbackCalendarDate = new CalendarDate(fallbackDate.getFullYear(), fallbackDate.getMonth() + 1, fallbackDate.getDate());
		return {
			start: fallbackCalendarDate,
			end: fallbackCalendarDate.add({ days: 1 })
		};
	}
};

/**
 * Creates year-month range values from a DateValue range
 * Formats months as two digits (e.g., "01", "02", etc.)
 */
export const createYearMonthRange = (dateRange: RangeValue<DateValue> | null) => {
	try {
		if (!dateRange) return undefined;

		// Subtract one day from the end date before creating year-month value
		const adjustedEndDate = dateRange.end.subtract({ days: 1 });

		return {
			start: Number(`${dateRange.start.year}${dateRange.start.month.toString().padStart(2, '0')}`),
			end: Number(`${adjustedEndDate.year}${adjustedEndDate.month.toString().padStart(2, '0')}`)
		};
	} catch (error) {
		// Only log exceptions for year-month range creation failures
		trackExceptionWithContext(
			error instanceof Error ? error : new Error('Year-month range creation failed'),
			{
				component: AppInsightsComponents.DATE_RANGE_HELPERS,
				additionalData: {
					errorType: AppInsightsErrorTypes.DATE_RANGE_CALCULATION_ERROR,
					hasDateRange: !!dateRange,
					startYear: dateRange?.start?.year,
					startMonth: dateRange?.start?.month,
					endYear: dateRange?.end?.year,
					endMonth: dateRange?.end?.month
				}
			}
		);
		return undefined;
	}
};

/**
 * Checks if the end date is covering live (in the current month)
 * @param date - The end date to check
 * @returns True if the end date is in the current month, false otherwise
 */
export const isCoveringLive = (date: Date, baseDate: Date): boolean => {
	try {
		const currentDate = baseDate || new Date();
		const currentYear = currentDate.getFullYear();
		const currentMonth = currentDate.getMonth();

		// Check if date is in the current month and year
		return date.getFullYear() > currentYear || (date.getFullYear() === currentYear && date.getMonth() > currentMonth);
	} catch (error) {
		// Only log exceptions for live coverage check failures
		trackExceptionWithContext(
			error instanceof Error ? error : new Error('Live coverage check failed'),
			{
				component: AppInsightsComponents.DATE_RANGE_HELPERS,
				additionalData: {
					errorType: AppInsightsErrorTypes.DATE_RANGE_VALIDATION_ERROR,
					dateYear: date?.getFullYear?.(),
					dateMonth: date?.getMonth?.(),
					baseDateYear: baseDate?.getFullYear?.(),
					baseDateMonth: baseDate?.getMonth?.()
				}
			}
		);
		return false;
	}
};

/**
 * Converts start and end dates to a formatted string
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns A formatted string representing the date range
 */
export const toStartDateEndDateString = (startDate?: Date | string, endDate?: Date | string): string => {
	try {
		if (!startDate || !endDate) {
			return '';
		}
		const formatted = new Date(startDate).toLocaleString('default', { month: 'short', year: 'numeric' }) + ' to ' + new Date(endDate).toLocaleString('default', { month: 'short', year: 'numeric' });
		return formatted;
	} catch (error) {
		// Only log exceptions for date string formatting failures
		trackExceptionWithContext(
			error instanceof Error ? error : new Error('Date range string formatting failed'),
			{
				component: AppInsightsComponents.DATE_RANGE_HELPERS,
				additionalData: {
					errorType: AppInsightsErrorTypes.DATE_RANGE_VALIDATION_ERROR,
					startDateType: typeof startDate,
					endDateType: typeof endDate,
					startDateValue: String(startDate),
					endDateValue: String(endDate)
				}
			}
		);
		return '';
	}
};